import { Box, Group, Progress, Stack, Text } from '@mantine/core';

import { FONT_WEIGHT_REGULAR, FONT_WEIGHT_SEMIBOLD } from '@constants';

interface BatchProgressIndicatorProps {
  status: string;
  value: number;
  totalWebUrls?: number;
  processedUrls?: number;
  error?: boolean;
  isAvailable?: boolean;
  displayValue?: string;
  barColor?: string;
}

const TEXTS: Record<string, string> = {
  'error-distance': 'Error Distance',
  'parsing-file': 'Parsing file',
  'sending-declarations': 'Sending declarations',
  'syncing-declarations': 'Customs sync',
  'validating-declarations': 'Validation in progress',
  'batch-initialized': 'Batch initialized',
  'invalidating-declarations': 'Invalidate declarations',
  'saving-db': 'Saving declarations',
  'scraping-done': 'Scraping done',
  'scraping-in-progress': 'Risk analysis progress',
  'updating-declarations': 'Updating declarations',
  'screenshot-in-progress': 'Getting screenshots',
};

export const BatchProgressIndicator = ({
  status,
  error = false,
  value,
  displayValue,
  processedUrls,
  totalWebUrls,
  barColor,
  isAvailable = true,
}: BatchProgressIndicatorProps) => {
  const progressColor = error ? 'red' : barColor || 'blue';

  const progressSize = error ? 'md' : 'sm';

  const renderValue = () => {
    if (!isAvailable) {
      return (
        <Text size="sm" fw={500} c="dimmed">
          Not available
        </Text>
      );
    }

    if (displayValue) {
      return (
        <Text size="sm" fw={500}>
          {displayValue}
        </Text>
      );
    }

    if (error) {
      return (
        <Text size="sm" fw={500} c="red">
          <span>{Math.trunc(value)}</span> of 100%
        </Text>
      );
    }

    return (
      <Text size="sm" fw={500} c="dimmed">
        {value !== 100 && `${Math.trunc(value)}%`}
      </Text>
    );
  };

  return (
    <Stack gap="xs" miw={200}>
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text
          size={error ? 'sm' : 'xs'}
          fw={error ? FONT_WEIGHT_REGULAR : FONT_WEIGHT_SEMIBOLD}
          tt={error ? 'initial' : 'uppercase'}
          c={error ? 'dark' : 'dimmed'}
        >
          {TEXTS[status] || ''}
        </Text>
        {renderValue()}
      </Group>

      {status === 'screenshot-in-progress' && (
        <Text size="xs" fw={500}>
          <Box component="span" c="blue">
            {processedUrls}
          </Box>{' '}
          / <Box component="span">{totalWebUrls}</Box>
        </Text>
      )}

      <Progress value={value} color={progressColor} size={progressSize} radius="sm" animated />
    </Stack>
  );
};
